import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  MenuItem,
  Switch,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import axios from '../../utils/axios';
import { notifySuccess } from '../../utils/notify';

const returnsSettingsEndpoint = '/settings/returns-receiving';

const emptyReturnsSettings = {
  returns_store_id: '',
  finished_goods_store_id: '',
  organization_id: '',
};

const normalizeReturnsSettings = (data) => {
  let payload = data?.data || data?.settings || data?.returns_receiving_settings || data || {};
  if (typeof payload.value_json === 'string') {
    try {
      payload = JSON.parse(payload.value_json);
    } catch (_) {
      payload = {};
    }
  }
  return {
    returns_store_id: String(payload.returns_store_id || ''),
    finished_goods_store_id: String(payload.finished_goods_store_id || ''),
    organization_id: String(payload.organization_id || ''),
  };
};

const normalizeStores = (data) => {
  const rows = Array.isArray(data) ? data : (Array.isArray(data?.stores) ? data.stores : []);
  return rows.map((store) => ({
    ...store,
    id: String(store.id || store.store_id || store.meta?.href || ''),
    name: store.name || store.pathName || store.store_name || store.id || 'Склад без названия',
  })).filter((store) => store.id);
};

export default function Installations() {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [whitelistInput, setWhitelistInput] = useState('');
  const [msStores, setMsStores] = useState([]);
  const [returnsSettings, setReturnsSettings] = useState(emptyReturnsSettings);
  const [returnsSettingsWarning, setReturnsSettingsWarning] = useState('');
  const [excludedAccounts, setExcludedAccounts] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [pnlAccountsWarning, setPnlAccountsWarning] = useState('');
  const [processTypes, setProcessTypes] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/settings/payroll');
      setEnabled(Boolean(data?.enabled));
      const wl = Array.isArray(data?.whitelist) ? data.whitelist : [];
      setWhitelistInput(wl.join(', '));
      const warnings = [];
      try {
        const { data: storesData } = await axios.get('/mswb/ms-stores', { silent: true });
        setMsStores(normalizeStores(storesData));
      } catch (_) {
        setMsStores([]);
        warnings.push('Список складов МойСклад пока недоступен.');
      }
      try {
        const { data: returnsData } = await axios.get(returnsSettingsEndpoint, { silent: true });
        setReturnsSettings(normalizeReturnsSettings(returnsData));
      } catch (_) {
        setReturnsSettings(emptyReturnsSettings);
        warnings.push('Backend endpoint для returns_receiving_settings пока не доступен.');
      }
      setReturnsSettingsWarning(warnings.join(' '));

      // P&L: исключаемые счета + список реально встречающихся счетов
      let pnlWarn = '';
      try {
        const { data: exData } = await axios.get('/settings/pnl-excluded-accounts', { silent: true });
        setExcludedAccounts(Array.isArray(exData?.accounts) ? exData.accounts.map(String) : []);
      } catch (_) {
        setExcludedAccounts([]);
      }
      try {
        const { data: accData } = await axios.get('/settings/payment-accounts', { silent: true });
        const accs = Array.isArray(accData?.accounts) ? accData.accounts : [];
        setPaymentAccounts(accs);
        if (accData?.meta?.column_missing) {
          pnlWarn = 'Номера счетов ещё не собраны (колонка появится после ближайшего прогона синхронизации МойСклад). Можно ввести номер счёта вручную.';
        }
      } catch (_) {
        setPaymentAccounts([]);
      }
      setPnlAccountsWarning(pnlWarn);

      try {
        const { data: typesData } = await axios.get('/ms/process-types', { silent: true });
        setProcessTypes(Array.isArray(typesData?.types) ? typesData.types : []);
      } catch (_) {
        try {
          const { data: saved } = await axios.get('/settings/production-types', { silent: true });
          const map = saved?.types && typeof saved.types === 'object' ? saved.types : {};
          setProcessTypes(Object.entries(map).map(([id, spec]) => ({
            id,
            name: id,
            href: '',
            requires_weight: Boolean(spec?.requires_weight),
          })));
        } catch (__) {
          setProcessTypes([]);
        }
      }
    } catch (_) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = async (e) => {
    const next = e.target.checked;
    setEnabled(next);
    try {
      await axios.put('/settings/payroll', { enabled: next, whitelist: parseWhitelist(whitelistInput) });
    } catch (_) {
      setEnabled(!next);
    }
  };

  const parseWhitelist = (text) => {
    return String(text || '')
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  };

  const handleSaveWhitelist = async () => {
    try {
      setLoading(true);
      const whitelist = parseWhitelist(whitelistInput);
      await axios.put('/settings/payroll', { enabled, whitelist });
    } catch (_) {
      /* notify в axios */
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReturnsSettings = async () => {
    try {
      setLoading(true);
      const payload = {
        returns_store_id: returnsSettings.returns_store_id,
        finished_goods_store_id: returnsSettings.finished_goods_store_id,
        organization_id: returnsSettings.organization_id,
      };
      await axios.put(returnsSettingsEndpoint, payload);
      setReturnsSettingsWarning('');
      notifySuccess('Настройки приёмки возвратов сохранены');
    } catch (_) {
      /* notify в axios */
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProcessWeight = async (typeId, next) => {
    const prev = processTypes;
    setProcessTypes((list) => list.map((type) => (
      String(type.id) === String(typeId) ? { ...type, requires_weight: next } : type
    )));
    try {
      const types = {};
      for (const type of processTypes) {
        types[String(type.id)] = {
          requires_weight: String(type.id) === String(typeId) ? next : Boolean(type.requires_weight),
        };
      }
      await axios.put('/settings/production-types', { types });
    } catch (_) {
      setProcessTypes(prev);
    }
  };

  const handleSaveExcludedAccounts = async () => {
    try {
      setLoading(true);
      const accounts = Array.from(new Set(excludedAccounts.map((s) => String(s).trim()).filter(Boolean)));
      await axios.put('/settings/pnl-excluded-accounts', { accounts });
      setExcludedAccounts(accounts);
      notifySuccess('Список исключаемых счетов сохранён');
    } catch (_) {
      /* notify в axios */
    } finally {
      setLoading(false);
    }
  };

  const accountLabel = (acc) => {
    const parts = [acc.account_number];
    if (acc.account_name) parts.push(acc.account_name);
    if (acc.agent_example) parts.push(acc.agent_example);
    return parts.filter(Boolean).join(' · ');
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Установки
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Настройки оплаты
        </Typography>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={handleToggle} disabled={loading} />}
          label="Тумблер больше не влияет на цех — оплата только по белому списку"
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Белый список товаров (ID из МойСклад). Если выпускаемый товар в списке и передан вес — 6 ₽/кг.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
            <TextField
              multiline
              minRows={2}
              size="small"
              sx={{ flex: 1, minWidth: 280 }}
              placeholder="id1, id2, id3"
              value={whitelistInput}
              onChange={(e) => setWhitelistInput(e.target.value)}
            />
            <Button variant="contained" onClick={handleSaveWhitelist} disabled={loading}>
              Сохранить
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Приёмка возвратов
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Склады используются при создании возврата покупателя, перемещении принятого товара и разборе через обратную техоперацию.
        </Typography>
        {returnsSettingsWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {returnsSettingsWarning}
          </Alert>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="flex-start">
          <TextField
            select
            label="Склад возвратов"
            size="small"
            sx={{ minWidth: 260, flex: 1 }}
            value={returnsSettings.returns_store_id}
            onChange={(e) => setReturnsSettings((prev) => ({ ...prev, returns_store_id: e.target.value }))}
            disabled={loading || msStores.length === 0}
            helperText="На этот склад backend создаёт возврат покупателя"
          >
            <MenuItem value="">Не выбран</MenuItem>
            {msStores.map((store) => (
              <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Склад готовой продукции"
            size="small"
            sx={{ minWidth: 260, flex: 1 }}
            value={returnsSettings.finished_goods_store_id}
            onChange={(e) => setReturnsSettings((prev) => ({ ...prev, finished_goods_store_id: e.target.value }))}
            disabled={loading || msStores.length === 0}
            helperText="Сюда перемещается товар при действии «Принять»"
          >
            <MenuItem value="">Не выбран</MenuItem>
            {msStores.map((store) => (
              <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Организация МС"
            size="small"
            sx={{ minWidth: 220 }}
            placeholder="UUID организации, если нужен"
            value={returnsSettings.organization_id}
            onChange={(e) => setReturnsSettings((prev) => ({ ...prev, organization_id: e.target.value }))}
            disabled={loading}
            helperText="Обязательно для документов МойСклад"
          />
          <Button
            variant="contained"
            onClick={handleSaveReturnsSettings}
            disabled={loading || !returnsSettings.returns_store_id || !returnsSettings.finished_goods_store_id || !returnsSettings.organization_id}
            sx={{ mt: { md: 0.25 } }}
          >
            Сохранить
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Исключаемые счета для P&L
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Платежи по этим расчётным счетам («переводы на свои счета») полностью исключаются из
          операционных расходов в отчёте о прибылях и убытках. Пустой список — исключений нет,
          в расходы попадают все платежи. Можно выбрать счёт из списка встречающихся или ввести
          номер вручную.
        </Typography>
        {pnlAccountsWarning && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {pnlAccountsWarning}
          </Alert>
        )}
        <Stack spacing={1.5}>
          <Autocomplete
            multiple
            freeSolo
            options={paymentAccounts.map((acc) => String(acc.account_number))}
            value={excludedAccounts}
            onChange={(_e, val) => setExcludedAccounts(val.map((s) => String(s).trim()).filter(Boolean))}
            getOptionLabel={(option) => {
              const acc = paymentAccounts.find((a) => String(a.account_number) === String(option));
              return acc ? accountLabel(acc) : String(option);
            }}
            renderOption={(props, option) => {
              const acc = paymentAccounts.find((a) => String(a.account_number) === String(option));
              return (
                <li {...props} key={option}>
                  <Box>
                    <Typography variant="body2">{acc ? accountLabel(acc) : option}</Typography>
                    {acc && (
                      <Typography variant="caption" color="text.secondary">
                        платежей: {acc.payments_count} · оборот: {Number(acc.total_amount).toLocaleString('ru-RU')} ₽
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const acc = paymentAccounts.find((a) => String(a.account_number) === String(option));
                return (
                  <Chip
                    variant="outlined"
                    label={acc ? accountLabel(acc) : option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Исключаемые расчётные счета"
                placeholder="Выберите счёт или введите номер и нажмите Enter"
                helperText="Исключение идёт по номеру счёта (agentAccount из МойСклад)"
              />
            )}
          />
          <Box>
            <Button variant="contained" onClick={handleSaveExcludedAccounts} disabled={loading}>
              Сохранить
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
          Весы по типу производства
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Если включено, на странице цеха для этого типа показываются весы и вес уходит в проведение.
          По умолчанию включено только для дробления и набивки — нарезку не включаем.
        </Typography>
        {processTypes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Типы производства пока недоступны</Typography>
        ) : (
          <Stack spacing={0.5}>
            {processTypes.map((type) => (
              <FormControlLabel
                key={type.id}
                control={(
                  <Switch
                    checked={Boolean(type.requires_weight)}
                    onChange={(event) => handleToggleProcessWeight(type.id, event.target.checked)}
                    disabled={loading}
                  />
                )}
                label={type.name || type.id}
              />
            ))}
          </Stack>
        )}
      </Paper>

    </Box>
  );
}
